import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import { FadeUp, FadeIn } from "../components/Animations";
import { motion } from "framer-motion";
import Testimonials from "../components/Testimonials";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://customer-assets.emergentagent.com/job_a31d91c6-928d-4105-9cc7-4850ef18f1bb/artifacts/fa16oiy8_shutterstock_2466476747.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a31d91c6-928d-4105-9cc7-4850ef18f1bb/artifacts/zj9kpzwc_JD%20Design%20Haus%20logo%20in%20grayscale%20%281%29.png";

const SERVICES = [
  {
    title: "Interior Design",
    desc: "Space planning, furniture selection, material curation.",
  },
  {
    title: "Project Management",
    desc: "Budget oversight, contractor coordination, timeline control.",
  },
  {
    title: "Full-Service Execution",
    desc: "From concept to completion. Every detail, handled.",
  },
];

export default function HomePage() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/projects`)
      .then((res) => setProjects(res.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ── Hero ── */}
      <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Luxury interior"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 -mt-32 md:mt-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.7, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.img
              src={LOGO_URL}
              alt="J Design Haus"
              className="h-64 md:h-80 lg:h-96 w-auto mx-auto"
              style={{ filter: "brightness(0) invert(1)" }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 text-sm md:text-base tracking-[0.3em] uppercase font-light text-bone/70"
          >
            Elevated Spaces. Crafted with Intention.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2.2 }}
            className="mt-10 scroll-indicator"
          >
            <ArrowDown className="text-bone/60" size={20} />
          </motion.div>
        </div>
      </section>

      {/* ── Intro ── */}
      <section className="relative py-24 md:py-32 px-6 md:px-16 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMG1hcmJsZSUyMHRleHR1cmUlMjBiYWNrZ3JvdW5kJTIwbWluaW1hbHxlbnwwfHx8fDE3NzYyMDA5MTN8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
            Scottsdale, Arizona
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <h2 className="font-serif text-3xl md:text-5xl font-light leading-snug max-w-4xl text-onyx">
            A design firm creating refined interiors rooted in architectural
            precision and editorial restraint.
          </h2>
        </FadeUp>
        <FadeUp delay={0.3}>
          <Link
            to="/about"
            data-testid="intro-learn-more"
            className="inline-flex items-center gap-3 mt-10 text-sm tracking-[0.15em] uppercase font-light text-onyx hover:text-taupe transition-colors"
          >
            Learn More <ArrowRight size={16} />
          </Link>
        </FadeUp>
        </div>
      </section>

      {/* ── Services Preview ── */}
      <section className="bg-onyx py-24 md:py-32 px-6 md:px-16">
        <div className="max-w-[1400px] mx-auto">
          <FadeUp>
            <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
              What We Do
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-bone/10">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.15}>
                <div
                  data-testid={`service-preview-${i}`}
                  className="border-b md:border-b-0 md:border-r last:border-r-0 border-bone/10 py-12 md:py-16 md:pr-12 md:pl-0 first:md:pl-0"
                  style={{ paddingLeft: i > 0 ? "3rem" : 0 }}
                >
                  <span className="text-bone/30 font-serif text-5xl font-light">
                    0{i + 1}
                  </span>
                  <h3 className="font-serif text-2xl md:text-3xl text-bone font-light mt-4 mb-4">
                    {s.title}
                  </h3>
                  <p className="text-sm font-light text-concrete leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.4}>
            <Link
              to="/services"
              data-testid="services-cta"
              className="inline-flex items-center gap-3 mt-12 text-sm tracking-[0.15em] uppercase font-light text-bone hover:text-taupe transition-colors"
            >
              View All Services <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* ── Portfolio Preview ── */}
      <section className="py-24 md:py-32 px-6 md:px-16 max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-8">
            Selected Work
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="font-serif text-3xl md:text-5xl font-light text-onyx mb-16">
            Recent Projects
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {projects.map((project, i) => (
            <FadeUp key={project.id} delay={i * 0.12}>
              <Link
                to={`/portfolio/${project.id}`}
                data-testid={`portfolio-preview-${i}`}
                className="group block"
              >
                <div className="img-scale w-full aspect-[4/3]">
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-4 flex justify-between items-start">
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl font-light text-onyx group-hover:text-taupe transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-xs tracking-[0.15em] uppercase text-taupe mt-1">
                      {project.location}
                    </p>
                  </div>
                  <ArrowRight
                    size={18}
                    className="text-concrete group-hover:text-onyx transition-colors mt-2"
                  />
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.4}>
          <Link
            to="/portfolio"
            data-testid="portfolio-view-all"
            className="inline-flex items-center gap-3 mt-14 text-sm tracking-[0.15em] uppercase font-light text-onyx hover:text-taupe transition-colors"
          >
            View All Projects <ArrowRight size={16} />
          </Link>
        </FadeUp>
      </section>

      {/* ── Philosophy ── */}
      <section className="relative py-24 md:py-36 px-6 md:px-16 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1566041510394-cf7c8fe21800?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMG1hcmJsZSUyMHRleHR1cmUlMjBiYWNrZ3JvdW5kJTIwbWluaW1hbHxlbnwwfHx8fDE3NzYyMDA5MTN8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="max-w-[1400px] mx-auto text-center relative z-10">
          <FadeUp>
            <blockquote className="font-serif text-3xl md:text-5xl lg:text-6xl font-light text-onyx leading-snug max-w-4xl mx-auto italic">
              "Design is not decoration. It is structure, flow, and intention."
            </blockquote>
          </FadeUp>
        </div>
      </section>
      <Testimonials />

      {/* ── CTA ── */}
      <section className="py-24 md:py-32 px-6 md:px-16 max-w-[1400px] mx-auto text-center">
        <FadeUp>
          <p className="text-xs tracking-[0.3em] uppercase text-taupe mb-6">
            Ready to Begin?
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <h2 className="font-serif text-4xl md:text-6xl font-light text-onyx mb-10">
            Start Your Project
          </h2>
        </FadeUp>
        <FadeUp delay={0.3}>
          <Link
            to="/contact"
            data-testid="home-cta-button"
            className="inline-block bg-onyx text-bone px-10 py-4 text-sm tracking-[0.2em] uppercase font-light hover:bg-taupe transition-colors duration-500"
          >
            Get in Touch
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
